import { useRef, useState } from 'react'
import { Button } from './Button'
import { FormError } from './Field'

/** 5 МБ — тот же потолок, что проверяет бэкенд (UploadsService.MAX_IMAGE_BYTES). */
const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const ACCEPT = ALLOWED_MIME.join(',')

/** Проверка файла до отправки: сеть зря не гоняем, ошибку показываем сразу. */
export function validateImageFile(file: File): string | null {
  if (file.size > MAX_BYTES) return 'Размер файла не должен превышать 5 МБ'
  if (!ALLOWED_MIME.includes(file.type)) {
    return 'Разрешены только изображения (JPEG, PNG, GIF, WebP)'
  }
  return null
}

/** data-URL файла — для превью до того, как сервер ответит. */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(file)
  })
}

/**
 * Загрузка картинки: превью + «Загрузить» / «Удалить».
 *
 * Кит не ходит в сеть сам — саму загрузку выполняет проп `onPick`, который
 * возвращает URL сохранённой картинки. Так один и тот же компонент работает и
 * там, где клуб уже создан (Настройки), и в мастере создания клуба.
 */
export function ImageUpload({
  value,
  onChange,
  onPick,
  onRemove,
  label = 'Загрузить',
  hint = 'JPEG, PNG, GIF или WebP, до 5 МБ',
  size = 64,
  disabled
}: {
  /** Текущий URL картинки (пусто — картинки нет). */
  value: string
  /** Вызывается с новым URL после успешной загрузки и с '' после удаления. */
  onChange: (url: string) => void
  /** Собственно загрузка: файл → URL. */
  onPick: (file: File) => Promise<string>
  /**
   * Зачистка в хранилище. Вызывается ТОЛЬКО для картинок, загруженных в этой же
   * сессии редактирования (см. uploadedHere) — уже сохранённые не трогаем.
   */
  onRemove?: (url: string) => Promise<void>
  label?: string
  hint?: React.ReactNode
  size?: number
  disabled?: boolean
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Превью из выбранного файла: показываем, пока идёт загрузка, иначе картинка
  // «появляется» только после ответа сервера и кнопка выглядит сломанной.
  const [preview, setPreview] = useState<string | null>(null)

  // URL'ы, загруженные ИМЕННО В ЭТОЙ сессии редактирования. Только их безопасно
  // вычищать из хранилища: картинка, пришедшая с сервера, уже сохранена в записи,
  // а форму могут закрыть по «Отмене» — и запись осталась бы со ссылкой в никуда.
  // Лишний файл в хранилище дешевле битой ссылки; он уйдёт с каскадом клуба.
  const uploadedHere = useRef<Set<string>>(new Set())

  const shown = preview ?? (value.trim() || null)

  /** Убрать из хранилища, но только если файл загружен здесь и сейчас. */
  const dropFromStorage = async (url: string): Promise<void> => {
    if (!url || !onRemove || !uploadedHere.current.has(url)) return
    uploadedHere.current.delete(url)
    await onRemove(url).catch(() => undefined)
  }

  const pick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    // Сбрасываем input сразу: иначе повторный выбор ТОГО ЖЕ файла не даст события.
    e.target.value = ''
    if (!file) return

    const invalid = validateImageFile(file)
    if (invalid) {
      setError(invalid)
      return
    }

    setError(null)
    setBusy(true)
    const previous = value.trim()
    try {
      setPreview(await readAsDataUrl(file).catch(() => null))
      const url = await onPick(file)
      uploadedHere.current.add(url)
      onChange(url)
      setPreview(null)
      // Перезагрузили поверх своей же свежей картинки (передумали, выбрали другую) —
      // прошлую прибираем, чтобы перебор вариантов не оставлял мусор.
      await dropFromStorage(previous)
    } catch (err) {
      setPreview(null)
      setError(err instanceof Error ? err.message : 'Не удалось загрузить файл')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (): Promise<void> => {
    const current = value.trim()
    setError(null)
    setBusy(true)
    try {
      onChange('')
      setPreview(null)
      await dropFromStorage(current)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="image-upload">
      <div className="image-upload__row">
        <div
          className="image-upload__preview"
          style={{ width: size, height: size }}
          aria-hidden={!shown}
        >
          {shown ? <img src={shown} alt="" /> : <span className="image-upload__empty">—</span>}
        </div>
        <div className="image-upload__actions">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            disabled={disabled || busy}
            onChange={pick}
          />
          <Button
            type="button"
            busy={busy}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Загрузка…' : shown ? 'Заменить' : label}
          </Button>
          {shown && !busy && (
            <Button type="button" variant="ghost" disabled={disabled} onClick={remove}>
              Удалить
            </Button>
          )}
        </div>
      </div>
      {error ? <FormError>{error}</FormError> : <div className="field__hint">{hint}</div>}
    </div>
  )
}
